import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Pagination,
  Typography,
  Stack,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
  Menu,
  Dialog,
  AppBar,
  Toolbar,
  Slide,
  IconButton,
  Divider,
  Button,
} from '@mui/material';
import {
  Search,
  BadgeCheck,
  Link2,
  Unlink,
  SquareDot,
  Building2,
  Filter,
  X,
} from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';

const FullScreenDialogTransition = React.forwardRef(function FullScreenDialogTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const formatCurrency = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const getStatusColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s.includes('COMPLETED') || s.includes('APPROVED')) return 'success';
  if (s.includes('CANCELED') || s.includes('CANCELLED') || s.includes('FAILED')) return 'error';
  if (s.includes('PENDING')) return 'warning';
  return 'default';
};

const ellipsis = (txt, max = 36) => {
  const s = String(txt || '');
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
};

export default function SquarePaymentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { refreshKey } = useRefresh();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [hotels, setHotels] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const perPage = 50;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchField, setSearchField] = useState('all');

  const [filters, setFilters] = useState({
    hotelId: '',
    status: 'ALL',
    linked: 'ALL', // ALL | LINKED | UNLINKED
    linkedSaleId: '', // ✅ NOVO: pos_sale_id
  });

  const [dateFilter, setDateFilter] = useState({ startDate: null, endDate: null });

  // ✅ filters UI (SalesPage-style)
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // ✅ link modal
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkTargetRow, setLinkTargetRow] = useState(null);
  const [linkSaleIdInput, setLinkSaleIdInput] = useState('');
  const [linking, setLinking] = useState(false);

  const [rowActionLoading, setRowActionLoading] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 450);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    filters.hotelId,
    filters.status,
    filters.linked,
    filters.linkedSaleId,
    dateFilter.startDate,
    dateFilter.endDate,
    searchField,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadFilters = async () => {
      try {
        const hotelsRes = await api.get('/hotels');
        if (cancelled) return;
        setHotels(Array.isArray(hotelsRes.data) ? hotelsRes.data : []);
      } catch (e) {
        console.error('Error loading hotels:', e);
      }
    };
    loadFilters();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchSquarePayments = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: debouncedSearch || '',
        search_field: searchField,
        hotel_id: filters.hotelId || '',
        status: filters.status || 'ALL',
        linked: filters.linked || 'ALL',
      };

      // ✅ filtro por pos_sale_id
      if (filters.linkedSaleId && String(filters.linkedSaleId).trim()) {
        params.pos_sale_id = String(filters.linkedSaleId).trim();
      }

      if (dateFilter.startDate) {
        const d = new Date(dateFilter.startDate);
        params.start_date = d.toISOString().slice(0, 10);
      }
      if (dateFilter.endDate) {
        const d = new Date(dateFilter.endDate);
        params.end_date = d.toISOString().slice(0, 10);
      }

      const res = await api.get('/admin/square-payments', { params });
      const data = res.data || {};
      const rows = Array.isArray(data.square_payments) ? data.square_payments : [];
      const pag = data.pagination || {};

      setItems(rows);
      setTotalItems(Number(pag.total || 0));
      setTotalPages(Number(pag.pages || 1));
    } catch (e) {
      console.error('Error loading square payments:', e);
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSquarePayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    debouncedSearch,
    searchField,
    filters.hotelId,
    filters.status,
    filters.linked,
    filters.linkedSaleId,
    dateFilter.startDate,
    dateFilter.endDate,
    refreshKey,
  ]);

  const handlePageChange = (_, value) => setPage(value);

  const linkedChip = (row) => {
    const linked = !!row?.pos_sale_id;
    if (linked) {
      return (
        <Chip
          size="small"
          icon={<Link2 size={16} />}
          label={`LINKED (#${row.pos_sale_id})`}
          color="success"
          variant="outlined"
          sx={{ fontWeight: 800 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        icon={<Unlink size={16} />}
        label="UNLINKED"
        color="error"
        variant="outlined"
        sx={{ fontWeight: 800 }}
      />
    );
  };

  const totals = useMemo(() => {
    const t = { subtotal: 0, tip: 0, fee: 0, total: 0 };
    for (const r of items) {
      t.subtotal += Number(r.subtotal || 0);
      t.tip += Number(r.tip || 0);
      t.fee += Number(r.fee || 0);
      t.total += Number(r.total || 0);
    }
    return t;
  }, [items]);

  const hasActiveFilters =
    !!debouncedSearch ||
    !!filters.hotelId ||
    !!filters.linkedSaleId ||
    (filters.status && filters.status !== 'ALL') ||
    (filters.linked && filters.linked !== 'ALL') ||
    !!dateFilter.startDate ||
    !!dateFilter.endDate ||
    (searchField && searchField !== 'all');

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setSearchField('all');
    setFilters({ hotelId: '', status: 'ALL', linked: 'ALL', linkedSaleId: '' });
    setDateFilter({ startDate: null, endDate: null });
    setPage(1);
    setFilterMenuAnchor(null);
    setFilterDialogOpen(false);
  };

  const openFilters = (e) => {
    if (isMobile) setFilterDialogOpen(true);
    else setFilterMenuAnchor(e.currentTarget);
  };

  const closeFilters = () => {
    setFilterMenuAnchor(null);
    setFilterDialogOpen(false);
  };

  const openLinkDialog = (row) => {
    setLinkTargetRow(row);
    setLinkSaleIdInput('');
    setLinkDialogOpen(true);
  };

  const closeLinkDialog = () => {
    if (linking) return;
    setLinkDialogOpen(false);
    setLinkTargetRow(null);
    setLinkSaleIdInput('');
  };

  const doLink = async () => {
    if (!linkTargetRow) return;
    const saleId = String(linkSaleIdInput || '').trim();

    if (!saleId || !/^\d+$/.test(saleId)) {
      alert('Sale ID inválido.');
      return;
    }

    setLinking(true);
    try {
      await api.post(`/admin/square-payments/${linkTargetRow.id}/link`, { pos_sale_id: Number(saleId) });
      setLinkDialogOpen(false);
      setLinkTargetRow(null);
      setLinkSaleIdInput('');
      await fetchSquarePayments();
    } catch (e) {
      console.error('Error linking:', e);
      alert(e?.response?.data?.msg || 'Erro ao linkar.');
    } finally {
      setLinking(false);
    }
  };

  const doUnlink = async (row) => {
    if (!row?.id) return;
    const ok = window.confirm(`Deslinkar este pagamento do sale #${row.pos_sale_id}?`);
    if (!ok) return;

    setRowActionLoading(row.id);
    try {
      await api.post(`/admin/square-payments/${row.id}/unlink`);
      await fetchSquarePayments();
    } catch (e) {
      console.error('Error unlinking:', e);
      alert(e?.response?.data?.msg || 'Erro ao deslinkar.');
    } finally {
      setRowActionLoading(null);
    }
  };

  const FiltersContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <FormControl size="small" fullWidth>
        <InputLabel>Hotel</InputLabel>
        <Select
          value={filters.hotelId}
          label="Hotel"
          onChange={(e) => setFilters((p) => ({ ...p, hotelId: e.target.value }))}
          startAdornment={
            <InputAdornment position="start">
              <Building2 size={18} />
            </InputAdornment>
          }
        >
          <MenuItem value="">All Hotels</MenuItem>
          {(hotels || []).map((h) => (
            <MenuItem key={h.id} value={h.id}>
              {h.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* ✅ NOVO: Linked Sale ID */}
      <TextField
        size="small"
        fullWidth
        label="Linked Sale ID"
        value={filters.linkedSaleId}
        onChange={(e) => setFilters((p) => ({ ...p, linkedSaleId: e.target.value }))}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        placeholder="ex: 12345"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Link2 size={18} />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
          >
            <MenuItem value="ALL">ALL</MenuItem>
            <MenuItem value="APPROVED">APPROVED</MenuItem>
            <MenuItem value="COMPLETED">COMPLETED</MenuItem>
            <MenuItem value="PENDING">PENDING</MenuItem>
            <MenuItem value="CANCELED">CANCELED</MenuItem>
            <MenuItem value="FAILED">FAILED</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>Link</InputLabel>
          <Select
            value={filters.linked}
            label="Link"
            onChange={(e) => setFilters((p) => ({ ...p, linked: e.target.value }))}
          >
            <MenuItem value="ALL">ALL</MenuItem>
            <MenuItem value="LINKED">LINKED</MenuItem>
            <MenuItem value="UNLINKED">UNLINKED</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <FormControl size="small" fullWidth>
        <InputLabel>Search Field</InputLabel>
        <Select value={searchField} label="Search Field" onChange={(e) => setSearchField(e.target.value)}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="payment_id">Payment ID</MenuItem>
          <MenuItem value="order_id">Order ID</MenuItem>
          <MenuItem value="pos_sale_id">Linked Sale ID</MenuItem>
          <MenuItem value="notes">Notes</MenuItem>
          <MenuItem value="merchant_id">Merchant ID</MenuItem>
          <MenuItem value="event_id">Event ID</MenuItem>
        </Select>
      </FormControl>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <DatePicker
            label="Start"
            value={dateFilter.startDate}
            onChange={(v) => setDateFilter((p) => ({ ...p, startDate: v }))}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <DatePicker
            label="End"
            value={dateFilter.endDate}
            onChange={(v) => setDateFilter((p) => ({ ...p, endDate: v }))}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );

  const SearchBar = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        p: 0,
        overflow: 'hidden',
        border: '2px solid transparent',
        backgroundImage: `
          linear-gradient(#fff, #fff),
          linear-gradient(180deg, #ec4899, #a855f7)
        `,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.92)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch" justifyContent="space-between">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch" flex={1}>
            <TextField
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              size="small"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearch('')} size="small" edge="end" sx={{ mr: -0.5 }}>
                      <X size={16} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'space-between', md: 'flex-end' }}
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                alignItems: 'center',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'auto auto' },
                justifyContent: { xs: 'stretch', md: 'flex-end' },
              }}
            >
              <Button
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                onClick={openFilters}
                startIcon={<Filter size={16} />}
                size="small"
                sx={{
                  height: 40,
                  fontWeight: 900,
                  borderRadius: 2,
                  width: { xs: '100%', md: 'auto' },
                  minWidth: { md: 110 },
                  whiteSpace: 'nowrap',
                }}
              >
                Filters
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={clearFilters}
                  startIcon={<X size={16} />}
                  size="small"
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    height: 40,
                    fontWeight: 900,
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Stack>
        </Stack>

        {/* DESKTOP: Menu */}
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={closeFilters}
          PaperProps={{ sx: { p: 2, borderRadius: 2, width: 460, maxWidth: 520 } }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography fontWeight={900}>Advanced Filters</Typography>
            <IconButton onClick={closeFilters} size="small">
              <X size={16} />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 1.5 }} />
          {FiltersContent}
          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="text" color="inherit" onClick={clearFilters} startIcon={<X size={16} />} sx={{ fontWeight: 800 }}>
              Clear
            </Button>
            <Button variant="contained" onClick={closeFilters} sx={{ fontWeight: 900 }}>
              Done
            </Button>
          </Stack>
        </Menu>

        {/* MOBILE: Fullscreen Dialog */}
        <Dialog fullScreen open={filterDialogOpen} onClose={closeFilters} TransitionComponent={FullScreenDialogTransition}>
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Filter size={18} />
                <Typography fontWeight={900}>Filters</Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                {hasActiveFilters && (
                  <Button color="inherit" onClick={clearFilters} sx={{ fontWeight: 900 }}>
                    Clear
                  </Button>
                )}
                <Button color="inherit" onClick={closeFilters} sx={{ fontWeight: 900 }}>
                  Close
                </Button>
              </Stack>
            </Toolbar>
          </AppBar>

          <Box sx={{ p: 2 }}>
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Advanced Filters
            </Typography>

            {FiltersContent}

            <Box sx={{ mt: 2 }}>
              <Button fullWidth variant="contained" onClick={closeFilters} sx={{ height: 44, borderRadius: 2, fontWeight: 900 }}>
                Apply
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Paper>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={1}>
        <Box sx={{ maxWidth: 1250, mx: 'auto' }}>
          {SearchBar}

          {/* Table */}
          <Box
            sx={{
              mt: 2,
              width: '100%',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              backgroundColor: '#fff',
            }}
          >
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, overflowX: 'visible', minWidth: '100%' }}>
              <Table
                stickyHeader
                sx={{
                  minWidth: 1200,
                  '& th, & td': { whiteSpace: 'nowrap', fontSize: { xs: 12, sm: 13 }, py: { xs: 1, sm: 1.25 } },
                  '& th': { fontWeight: 900, backgroundColor: '#f8f9fa' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Hotel</TableCell>
                    <TableCell>Payment ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sale ID</TableCell>
                    <TableCell>Link/Unlink</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right">Tip</TableCell>
                    <TableCell align="right">Fee</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          Loading Square payments...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (items || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No Square payments found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (items || []).map((row) => {
                      const linked = !!row?.pos_sale_id;
                      const busy = rowActionLoading === row.id;

                      return (
                        <TableRow key={row.id} hover sx={{ '&:hover': { backgroundColor: '#f9fbff' } }}>
                          <TableCell>{row.date || '—'}</TableCell>
                          <TableCell>{row.hotel_name || '—'}</TableCell>
                          <TableCell>
                            <Chip
                            
                              size="small"
                              icon={<SquareDot size={16} />}
                              label={String(row.square_payment_id || '').slice(0, 10)}
                              variant="outlined"
                              sx={{ fontWeight: 800, fontFamily: 'monospace' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                            sx={{ fontWeight: 800 }}
                            size="small" label={row.status || '—'} color={getStatusColor(row.status)} />
                          </TableCell>

                          <TableCell>
                            
                            {linkedChip(row)}</TableCell>

                          <TableCell>
                            {linked ? (
                              <Tooltip title={busy ? 'Working...' : 'Unlink this payment'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={busy}
                                    onClick={() => doUnlink(row)}
                                    sx={{ border: '1px solid rgba(239,68,68,0.35)', borderRadius: 2 }}
                                  >
                                    <Unlink size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Link to a Sale ID">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => openLinkDialog(row)}
                                    sx={{ border: '1px solid rgba(34,197,94,0.35)', borderRadius: 2 }}
                                  >
                                    <Link2 size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>

                          <TableCell align="right">{formatCurrency(row.subtotal)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.tip)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.fee)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900 }}>
                            {formatCurrency(row.total)}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={row.notes || ''}>
                              <span style={{ fontFamily: 'monospace' }}>{row.notes ? ellipsis(row.notes, 42) : '—'}</span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>

                {!loading && (items || []).length > 0 && (
                  <TableBody>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell colSpan={6} align="right" sx={{ fontWeight: 900 }}>
                        Totals ({items.length})
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>
                        {formatCurrency(totals.subtotal)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>
                        {formatCurrency(totals.tip)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>
                        {formatCurrency(totals.fee)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: '#1976d2' }}>
                        {formatCurrency(totals.total)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            mt={2}
            width="100%"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Showing {items.length} of {totalItems} square payments
                {hasActiveFilters && <span style={{ fontWeight: 900 }}> (filtered)</span>}
              </Typography>

              {hasActiveFilters ? (
                <Chip
                  size="small"
                  label="FILTERED"
                  color="primary"
                  variant="outlined"
                  icon={<BadgeCheck size={16} />}
                  sx={{ fontWeight: 900 }}
                />
              ) : null}
            </Stack>

            <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" shape="rounded" />
          </Stack>
        </Box>

        {/* ✅ LINK DIALOG */}
        <Dialog open={linkDialogOpen} onClose={closeLinkDialog} maxWidth="xs" fullWidth>
          <Box sx={{ p: 2 }}>
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Link Square Payment to Sale ID
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Payment: <b>{String(linkTargetRow?.square_payment_id || '').slice(0, 14)}</b>
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Sale ID"
              value={linkSaleIdInput}
              onChange={(e) => setLinkSaleIdInput(e.target.value)}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              placeholder="ex: 12345"
              sx={{ mb: 2 }}
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={closeLinkDialog} disabled={linking} sx={{ fontWeight: 900 }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={doLink} disabled={linking} sx={{ fontWeight: 900 }}>
                {linking ? 'Linking...' : 'Link'}
              </Button>
            </Stack>
          </Box>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
